/**
 * Замечания владельца к звонкам голосового робота — для модели, которая разбирает их в чате сессии
 * (`voice-lab-remarks`) и ставит статус «Обработано / Внесено / Учтено».
 *
 * - `RemarkForm` — под репликой или плеером: голос одной кнопкой (после «Стоп» замечание СРАЗУ сохраняется
 *   с голосом, расшифровка — фоном) и необязательный текст (Enter — сохранить).
 * - `RemarkChip` — сохранённое замечание: статус, текст / расшифровка, голос, «Удалить» до разбора.
 * - `RemarksTab` — список замечаний звонка, `RemarksPage` — все замечания стенда (`?tab=remarks`).
 * Спека `docs/specs/2026-10-27-voice-lab-remarks-design.md`.
 */
import { useGetIdentity } from "@refinedev/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { MessageSquarePlus, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router";

import { VoiceControl, useVoice, voiceBusy } from "@/components/voice/VoiceInput";
import type { CurrentUser } from "@/lib/providers";
import { uploadVoice } from "@/lib/voice";
import {
  OUTCOMES,
  addLabRemark,
  addScenarioRemark,
  addTemplateRemark,
  deleteLabRemark,
  parseScenarioRoom,
  parseTemplateRoom,
  templateHref,
  labRemarksQuery,
  patchLabRemark,
  type LabRemark,
} from "@/lib/voiceLab";

export const мс = (t: number) => `${Math.floor(t / 60)}:${String(Math.floor(t % 60)).padStart(2, "0")}`;

const ЦВЕТ: Record<LabRemark["status"], string> = {
  waiting_voice: "border-border text-fg-muted",
  voice_failed: "border-err/50 text-err",
  new: "border-accent/60 text-fg",
  proposed: "border-warn/60 text-fg",
  processed: "border-border text-fg-muted",
  applied: "border-ok/60 text-ok",
  noted: "border-ok/40 text-fg-muted",
  rejected: "border-border text-fg-muted line-through",
};
const ОТВЕТ = [
  ["seen", "Что было видно"],
  ["cause", "Почему так"],
  ["change", "Что изменили"],
  ["version", "Версия"],
] as const;

/** Ответ на разбор замечания — под ним: что было видно, почему робот так сделал, что изменили и где. */
function Ответ({ r }: { r: LabRemark }) {
  const поля = ОТВЕТ.filter(([к]) => r.answer?.[к]);
  if (!поля.length) return null;
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 border-t border-border/70 pt-1" data-annot="ответ на замечание">
      {поля.map(([к, имя]) => (
        <div key={к} className="contents">
          <dt className="text-[11.5px] text-fg-muted">{имя}</dt>
          <dd className={`whitespace-pre-wrap ${к === "version" ? "font-mono text-[11.5px]" : ""}`}>{r.answer![к]}</dd>
        </div>
      ))}
    </dl>
  );
}
const ПРАВИТЬ = new Set(["waiting_voice", "voice_failed", "new"]);

export function Статус({ r }: { r: LabRemark }) {
  return (
    <span className={`rounded-full border px-1.5 py-px text-[11px] ${ЦВЕТ[r.status] ?? "border-border"}`}>
      {r.status_label}
    </span>
  );
}

/** Форма замечания: голос сохраняет замечание сразу после «Стоп», текст — по Enter / «Сохранить». */
export function RemarkForm({
  room,
  turn,
  t,
  onClose,
  onSaved,
  scenario,
  template,
}: {
  room: string;
  turn?: number | null;
  t?: number | null;
  /** Замечание к версии сценария («Сценарии»): к выделенному куску `quote` или к версии целиком. */
  scenario?: { line: string; name: string; quote: string };
  /** Замечание к шаблону AI «Чатов»: к версии `version`, к куску `quote` или к шаблону целиком. */
  template?: { id: number; version: number; quote: string };
  onClose: () => void;
  /** Замечание сохранено: номер и текст (кнопка AI «Чатов» отклоняет по нему черновик). */
  onSaved?: (id: number, text: string) => void;
}) {
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const textRef = useRef(text);
  textRef.current = text;
  const обновить = () => {
    void qc.invalidateQueries({ queryKey: ["voice-lab-call", room] });
    void qc.invalidateQueries({ queryKey: ["voice-lab-remarks"] });
    if (scenario) void qc.invalidateQueries({ queryKey: ["voice-lab-scenarios"] });
  };
  const добавить = (text: string, voice_id?: number) =>
    template
      ? addTemplateRemark(template.id, { version: template.version, quote: template.quote, text, voice_id: voice_id ?? null })
      : scenario
      ? addScenarioRemark(scenario.line, scenario.name, { quote: scenario.quote, text, voice_id: voice_id ?? null })
      : addLabRemark(room, { turn: turn ?? null, t: t ?? null, text, voice_id: voice_id ?? null });
  const voice = useVoice({
    onDone: async (blob, seconds) => {
      const v = await uploadVoice(blob, seconds);
      const r = await добавить(textRef.current, v.id);
      onSaved?.(r.id, textRef.current || "голосом");
      обновить();
      onClose();
    },
  });
  const сохранить = async () => {
    if (!text.trim() || busy) return;
    setBusy(true);
    setError("");
    try {
      const r = await добавить(text);
      onSaved?.(r.id, text);
      обновить();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Не сохранилось");
    } finally {
      setBusy(false);
    }
  };
  const занято = busy || voiceBusy(voice.v);
  return (
    <div
      className="flex w-full max-w-xl flex-col gap-1.5 rounded-[var(--radius-ui)] border border-accent/50 bg-bg p-2 text-[12.5px]"
      data-annot="форма замечания"
    >
      <div className="text-[11.5px] text-fg-muted">
        Замечание для модели{" "}
        {template
          ? template.quote
            ? `к выделенному тексту шаблона v${template.version}`
            : `к шаблону v${template.version} целиком`
          : scenario
          ? scenario.quote
            ? "к выделенному тексту версии"
            : "к версии целиком"
          : turn != null
            ? "к этой реплике"
            : t != null
              ? `к моменту ${мс(t)} записи`
              : ""}{" "}
        · голос
        сохраняется сразу после «Стоп», текст — необязателен
      </div>
      <textarea
        autoFocus
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            void сохранить();
          }
          // идёт запись голоса — Esc её не выбрасывает: «Стоп» отправит
          if (e.key === "Escape" && !voiceBusy(voice.v)) onClose();
        }}
        rows={2}
        placeholder="Что не так или что сделать иначе… (Enter — сохранить, Shift+Enter — новая строка)"
        className="w-full resize-y rounded-[var(--radius-ui)] border border-border bg-bg-subtle px-2 py-1.5 outline-none focus:border-accent"
      />
      <div className="flex flex-wrap items-center gap-2">
        <VoiceControl voice={voice} disabled={busy} sentText="Голос ушёл — замечание сохранено" />
        <div className="ml-auto flex gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={voiceBusy(voice.v)}
            title={voiceBusy(voice.v) ? "Идёт запись — нажмите «Стоп», голос сохранится" : undefined}
            className="rounded-full px-3 py-1 text-fg-muted hover:text-fg disabled:opacity-40"
          >
            Отмена
          </button>
          <button
            type="button"
            disabled={!text.trim() || занято}
            onClick={() => void сохранить()}
            className="rounded-full bg-accent px-3 py-1 font-medium text-white disabled:opacity-50"
          >
            {busy ? "Сохраняю…" : "Сохранить"}
          </button>
        </div>
      </div>
      {error && <div className="text-err">{error}</div>}
    </div>
  );
}

/** Сохранённое замечание у реплики, в списке или во вкладке. */
export function RemarkChip({
  r,
  seek,
  showQuote,
}: {
  r: LabRemark;
  seek?: ((t: number) => void) | undefined;
  showQuote?: boolean;
}) {
  const qc = useQueryClient();
  const { data: user } = useGetIdentity<CurrentUser>();
  const [правка, setПравка] = useState<string | null>(null);
  const своё = user?.id === r.user_id && ПРАВИТЬ.has(r.status);
  const обновить = () => {
    void qc.invalidateQueries({ queryKey: ["voice-lab-call", r.room] });
    void qc.invalidateQueries({ queryKey: ["voice-lab-remarks"] });
  };
  return (
    <div
      className="flex w-full max-w-xl flex-col gap-1 rounded-[var(--radius-ui)] border border-accent/40 bg-warn-bg/60 px-2.5 py-1.5 text-[12.5px]"
      data-annot="замечание"
    >
      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11.5px] text-fg-muted">
        <MessageSquarePlus size={13} aria-hidden />
        <Статус r={r} />
        {r.t != null &&
          (seek ? (
            <button type="button" onClick={() => seek(r.t!)} className="tabular-nums text-accent hover:underline">
              {мс(r.t)}
            </button>
          ) : (
            <span className="tabular-nums">{мс(r.t)}</span>
          ))}
        <span>{r.date.slice(8, 10)}.{r.date.slice(5, 7)} {r.date.slice(11, 16)}</span>
        {своё && правка == null && (
          <>
            <button type="button" onClick={() => setПравка(r.text)} className="text-accent hover:underline">
              {r.text ? "Изменить текст" : "Дописать текст"}
            </button>
            <button
              type="button"
              title="Удалить замечание"
              onClick={() => void deleteLabRemark(r.id).then(обновить)}
              className="ml-auto grid size-6 place-items-center rounded-full hover:bg-bg-hover"
            >
              <Trash2 size={13} aria-hidden />
              <span className="sr-only">Удалить</span>
            </button>
          </>
        )}
      </div>
      {showQuote && r.quote && (
        <div className="border-l border-border pl-2 text-fg-muted">
          {parseScenarioRoom(r.room) || parseTemplateRoom(r.room) ? "В тексте" : r.who === "agent" ? "Робот" : "Собеседник"}: {r.quote}
        </div>
      )}
      {правка != null ? (
        <div className="flex flex-col gap-1">
          <textarea
            autoFocus
            value={правка}
            rows={2}
            onChange={(e) => setПравка(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                void patchLabRemark(r.id, правка).then(() => {
                  setПравка(null);
                  обновить();
                });
              }
              if (e.key === "Escape") setПравка(null);
            }}
            className="w-full resize-y rounded-[var(--radius-ui)] border border-border bg-bg px-2 py-1 outline-none focus:border-accent"
          />
          <span className="text-[11px] text-fg-muted">Enter — сохранить, Esc — отмена</span>
        </div>
      ) : (
        r.text && <div className="whitespace-pre-wrap">{r.text}</div>
      )}
      {r.voice && (
        <div className="flex flex-wrap items-center gap-2">
          <audio src={r.voice.url} controls preload="none" className="h-7 max-w-[240px]" />
          {r.transcript ? (
            <span className="whitespace-pre-wrap text-fg">«{r.transcript}»</span>
          ) : r.status === "waiting_voice" ? (
            <span className="text-fg-muted">расшифровывается…</span>
          ) : r.status === "voice_failed" ? (
            <span className="text-err">не расшифровалось{r.voice.error ? `: ${r.voice.error}` : ""} — допишите текст</span>
          ) : null}
        </div>
      )}
      <Ответ r={r} />
    </div>
  );
}

/** Вкладка «Замечания» карточки звонка: к репликам — с цитатой, к моменту записи — с таймкодом. */
export function RemarksTab({ remarks, seek }: { remarks: LabRemark[]; seek?: ((t: number) => void) | undefined }) {
  if (!remarks.length)
    return (
      <div className="text-[12.5px] text-fg-muted">
        Замечаний нет. Наведите на реплику и нажмите «Замечание» или поставьте запись на паузу и нажмите «Замечание на
        …» у плеера.
      </div>
    );
  return (
    <section className="flex max-w-3xl flex-col gap-2" data-annot="замечания звонка">
      {remarks.map((r) => (
        <RemarkChip key={r.id} r={r} seek={seek} showQuote />
      ))}
    </section>
  );
}

const ОТБОР = [
  ["new,voice_failed,waiting_voice", "Ждут разбора"],
  ["proposed", "Предложено решение"],
  ["processed,applied,noted,rejected", "Разобраны"],
  ["", "Все"],
] as const;

const КАНАЛ = [
  ["", "Все каналы"],
  ["voice", "Голос"],
  ["chat", "Чат"],
] as const;

/** Все замечания стенда (`?tab=remarks`): отбор по статусу и каналу (голос — звонки, чат — переписка бота;
 * у каждого канала своя линия сценариев), клик — карточка звонка на вкладке замечаний. Отбор — в адресе:
 * `status=new|proposed|done|all`, `channel=voice|chat`, `remark=<id>` — подсветить это замечание (тогда
 * по умолчанию «Все»: разобранное в «Ждут разбора» не видно — владелец 03.11.26 не нашёл своё). */
const В_АДРЕС: Record<string, string> = {
  "new,voice_failed,waiting_voice": "new",
  proposed: "proposed",
  "processed,applied,noted,rejected": "done",
  "": "all",
};
export function RemarksPage() {
  const [params, setParams] = useSearchParams();
  const выбрано = Number(params.get("remark")) || null;
  const изАдреса = Object.entries(В_АДРЕС).find(([, v]) => v === params.get("status"))?.[0];
  const [отбор, setОтборState] = useState<string>(изАдреса ?? (выбрано ? "" : ОТБОР[0][0]));
  const [канал, setКаналState] = useState<string>(
    ["voice", "chat"].includes(params.get("channel") ?? "") ? (params.get("channel") as string) : "",
  );
  const вАдрес = (ключ: string, значение: string) =>
    setParams(
      (had) => {
        const p = new URLSearchParams(had);
        if (значение) p.set(ключ, значение);
        else p.delete(ключ);
        p.delete("remark");
        return p;
      },
      { replace: true },
    );
  const setОтбор = (код: string) => {
    setОтборState(код);
    вАдрес("status", В_АДРЕС[код] ?? "");
  };
  const setКанал = (код: string) => {
    setКаналState(код);
    вАдрес("channel", код);
  };
  // Замечание из ссылки («Чаты» → вкладка «AI») — прокрутить к нему, когда список пришёл.
  const нашли = useRef(false);
  const q = useQuery({
    ...labRemarksQuery(отбор, канал),
    refetchInterval: (query) =>
      query.state.data?.remarks.some((r) => r.status === "waiting_voice") && !document.hidden ? 5_000 : false,
  });
  useEffect(() => {
    if (!выбрано || нашли.current || !q.data) return;
    const el = document.getElementById(`remark-${выбрано}`);
    if (el) {
      нашли.current = true;
      el.scrollIntoView({ block: "center" });
    }
  }, [выбрано, q.data]);
  return (
    <section className="flex max-w-4xl flex-col gap-3" data-annot="замечания к звонкам">
      <div className="flex flex-wrap items-center gap-2 text-[12.5px]">
        {ОТБОР.map(([код, имя]) => (
          <button
            key={код}
            type="button"
            onClick={() => setОтбор(код)}
            className={`rounded-full border px-3 py-1 ${отбор === код ? "border-accent bg-accent/10 text-fg" : "border-border text-fg-muted hover:text-fg"}`}
          >
            {имя}
          </button>
        ))}
        <span className="mx-1 h-4 w-px bg-border" aria-hidden />
        {КАНАЛ.map(([код, имя]) => (
          <button
            key={код}
            type="button"
            onClick={() => setКанал(код)}
            className={`rounded-full border px-3 py-1 ${канал === код ? "border-accent bg-accent/10 text-fg" : "border-border text-fg-muted hover:text-fg"}`}
            data-annot="канал замечаний"
          >
            {имя}
          </button>
        ))}
        <span className="text-[11.5px] text-fg-muted">
          Разбирает модель в чате сессии: предлагает решение с ответом (что видно, почему, что изменить) → после
          вашего слова Внесено (с версией инструкции) · Отклонено · Обработано · Учтено
        </span>
      </div>
      {q.isError && <div className="text-[12.5px] text-err">Замечания не загрузились</div>}
      {q.data && !q.data.remarks.length && <div className="text-[12.5px] text-fg-muted">Замечаний нет</div>}
      {q.data?.remarks.map((r) => {
        const сц = parseScenarioRoom(r.room);
        const шб = parseTemplateRoom(r.room);
        return (
        <div
          key={r.id}
          id={`remark-${r.id}`}
          className={`flex flex-col gap-1 ${r.id === выбрано ? "rounded-[var(--radius-ui)] outline outline-2 outline-offset-4 outline-accent" : ""}`}
        >
          {шб ? (
            <Link to={templateHref(шб.id, true)} className="w-fit text-[12px] text-accent hover:underline">
              Шаблон AI #{шб.id} · v{шб.version}
              {r.quote ? " · к выделенному тексту" : " · целиком"}
            </Link>
          ) : сц ? (
            <Link
              to={`/voice-lab?tab=scenarios&line=${encodeURIComponent(сц.line)}&v=${сц.name}`}
              className="w-fit text-[12px] text-accent hover:underline"
            >
              Сценарий {сц.line} · {сц.name}
              {r.quote ? " · к выделенному тексту" : " · к версии целиком"}
            </Link>
          ) : (
          <Link
            to={r.chat_id ? `/chats/${r.chat_id}?panel=ai` : `/voice-lab?tab=calls&call=${r.room}&ctab=remarks`}
            className="w-fit text-[12px] text-accent hover:underline"
          >
            {r.chat_id
              ? `Черновик кнопки AI · чат ${r.chat_id}`
              : r.call
                ? `${r.call.name ?? "—"} · ${r.call.started.slice(8, 10)}.${r.call.started.slice(5, 7)} ${r.call.started.slice(11, 16)} · ${r.call.model} · ${OUTCOMES[r.call.outcome] ?? r.call.outcome}`
                : r.room}
            {r.turn != null ? ` · реплика ${r.turn + 1}` : r.t != null ? ` · ${мс(r.t)} записи` : ""}
            {r.call?.prompt && <span className="text-fg-muted"> · {r.call.prompt}</span>}
          </Link>
          )}
          <RemarkChip r={r} showQuote />
        </div>
        );
      })}
    </section>
  );
}
